import type { SnapRoundingOptions } from '@calculations/round';
import { PriceSegmentContext } from '@snap-types/calculations/price-segment-context';
import { ProductComplexityRanking } from '@snap-types/models/enums';
import { SnapSuggestionStatus } from '@snap-types/models/sort-status';
import { dec, Decimal } from '@snap-types/types/decimal';

import { calculate } from './calculate';
import {
  CAPPriceSegmentation,
  type CAPPriceSegmentationPriceStrategy,
  type CAPPriceSegmentationPriceStrategyInput,
  type CAPPriceSegmentationPriceStrategyResult,
} from './types';

export function runCAPPriceSegmentationStrategy(
  strategy: CAPPriceSegmentationPriceStrategy,
  input: CAPPriceSegmentationPriceStrategyInput,
  roundingOptions: SnapRoundingOptions,
): CAPPriceSegmentationPriceStrategyResult {
  const { targetGrossMargin, segments } = configureStrategyForContext(
    strategy.config,
    input.params.context,
    input.params.lastWin?.grossMargin ?? null,
  );
  const {
    unitCost,
    lastWin,
    context,
    quantity,
    requiresUnsupportedCalculations,
  } = input.params;

  if (!unitCost) {
    return {
      name: CAPPriceSegmentation,
      isSuccess: false,
      data: {
        unitCost: null,
        lastWin: null,
        context,
        results: [],
      },
    };
  }

  const {
    quantity: _,
    unitPrice,
    baseGrossMargin,
    baseGrossMarginSource,
    grossMargin,
    baseUnitPrice,
    priceAdjustment,
    priceAdjustmentAmount,
    results,
  } = calculate(
    {
      quantity,
      targetGrossMargin,
      segments,
      unitCost,
      lastWin: lastWin ?? null,
      context,
    },
    roundingOptions,
  );

  const hasUnreliableCosts = requiresUnsupportedCalculations.length > 0;

  return {
    name: CAPPriceSegmentation,
    isSuccess: true,
    suggestedPriceStatus: hasUnreliableCosts
      ? SnapSuggestionStatus.EXCLUDED
      : SnapSuggestionStatus.ACCEPTED,
    quantity,
    unitPrice,
    initialMarginSource: baseGrossMarginSource,
    lastWinOrderLineId: lastWin?.id,
    data: {
      unitCost,
      grossMargin,
      baseGrossMargin,
      baseGrossMarginSource,
      baseUnitPrice,
      priceAdjustment,
      priceAdjustmentAmount,
      lastWin,
      context,
      results,
    },
  };
}

function configureStrategyForContext(
  config: CAPPriceSegmentationPriceStrategy['config'],
  context: PriceSegmentContext,
  lastWinGrossMargin: Decimal | null,
) {
  const marginOverride = getCustomTargetGrossMargin(
    {
      marketIndustry: context.marketIndustry,
      productComplexity: context.productComplexity,
    },
    lastWinGrossMargin,
  );

  return {
    ...config,
    targetGrossMargin: marginOverride ?? config.targetGrossMargin,
  };
}

const CAP_COMPLEXITY_BASE_MARGIN = {
  [ProductComplexityRanking.Simple]: dec(0.3),
  [ProductComplexityRanking.Moderate]: dec(0.35),
  [ProductComplexityRanking.Complex]: dec(0.4),
};

const CAP_COMPLEXITY_HIGH_MARGIN = {
  [ProductComplexityRanking.Simple]: dec(0.35),
  [ProductComplexityRanking.Moderate]: dec(0.4),
  [ProductComplexityRanking.Complex]: dec(0.5),
};

const CAP_HIGH_MARGIN_INDUSTRIES = ['AEROSPACE', 'DEFENSE'];

export function getCustomTargetGrossMargin(
  {
    marketIndustry,
    productComplexity,
  }: {
    marketIndustry?: string | null;
    productComplexity?: ProductComplexityRanking | null;
  },
  lastWinGrossMargin: Decimal | null,
): Decimal | null {
  const industry = marketIndustry?.toUpperCase();
  const complexity = productComplexity ?? ProductComplexityRanking.Moderate;

  let customMargin: Decimal = CAP_COMPLEXITY_BASE_MARGIN[complexity];

  if (industry && CAP_HIGH_MARGIN_INDUSTRIES.includes(industry)) {
    customMargin = CAP_COMPLEXITY_HIGH_MARGIN[complexity];
  }

  if (lastWinGrossMargin && lastWinGrossMargin.gt(customMargin)) {
    return null;
  }

  return customMargin;
}
